import React, { useEffect, useRef, useState, useTransition } from 'react';
import Header from '../../components/Header';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import { useParams, useSearchParams } from 'react-router';
import { callAPIAsync, getChatThread } from '../../utils/api';
import { useAppSelector } from '../../store/hooks';
import ErrorDisplay from '../../components/ErrorDisplay';
import Footer from '../../components/Footer';
import { isLegacyOp, removeOpSuffix } from '../../utils/operations';
import { useLogto } from '@logto/react';
import ChatBuilder from '../../components/ChatBuilder';
import { DocumentResponse } from '../../types/chat';
import { Download } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';

const ChatViewer: React.FC = () => {
	const params = useParams();
	const logto = useLogto();
	const [searchParams] = useSearchParams();
	const viewerRef = useRef<HTMLDivElement>(null);

	const userRoles = useAppSelector((state) => state.user.roles);

	const [jsonRes, setJsonRes] = useState<DocumentResponse | undefined>();
	const [loadingError, setLoadingError] = useState<string>('');
	const [pageLoading, startTransition] = useTransition();

	const pipelineType = params.pipelineType || 'cellebrite-xml';
	const container = params.container;
	const fileName = params.fileName;
	const page = searchParams.get('page');
	const messageId = searchParams.get('messageId');

	useEffect(() => {
		startTransition(async () => {
			if (container && fileName) {
				if (
					userRoles.some((role) => role.endsWith('-view') && removeOpSuffix(role.toLocaleLowerCase()) === container.toLocaleLowerCase()) ||
					userRoles.some(
						// for legacy operations
						(role) => isLegacyOp(role) && role.toLocaleLowerCase() === container.toLocaleLowerCase()
					)
				) {
					await callAPIAsync(logto, async (token) => {
						try {
							const res = await getChatThread(token, container, fileName, page, pipelineType, messageId);
							if (res.ok) {
								setJsonRes(await res.json());
							} else {
								setLoadingError(`Could not get chat thread: ${res.status} ${res.statusText}`);
							}
						} catch (e) {
							setLoadingError(`Could not load chat thread: ${e}`);
						}
					});
				} else {
					setLoadingError('You are not allowed to access data for this operation');
				}
			}
		});
	}, [page, messageId]);

	const printDoc = useReactToPrint({
		contentRef: viewerRef,
		documentTitle: `${container}_${fileName}`,
	});

	const getChatDisplay = () => {
		if (pageLoading) {
			return <CircularProgress variant="indeterminate" size={120} sx={{ padding: 4 }} aria-label="Downloading Chat Progress Spinner" />;
		} else {
			if (loadingError) {
				return (
					<ErrorDisplay
						summaryText={`Could not fetch file with name '${fileName}' from operation '${container}'`}
						errorText={String(loadingError)}
					/>
				);
			} else if (jsonRes) {
				return (
					<Stack direction="column" width="100%" height="100%">
						<Button
							fullWidth
							size="large"
							variant="text"
							sx={{ fontSize: '1.5em' }}
							startIcon={<Download />}
							onClick={() => printDoc()}
							data-testid={'print-thread-button'}
						>
							Download PDF
						</Button>
						<ChatBuilder
							pipelineType={pipelineType}
							viewerRef={viewerRef}
							caseReference={container || ''}
							chatId={fileName || ''}
							messageId={messageId}
							documentResponse={jsonRes}
							hourGap={3}
						/>
					</Stack>
				);
			}
		}
	};

	return (
		<>
			<Header />
			<Box
				width={'100vw'}
				height={'80vh'}
				display={!pageLoading && jsonRes ? undefined : 'flex'}
				justifyContent={'center'}
				alignItems={'center'}
			>
				{getChatDisplay()}
			</Box>
			<Footer />
		</>
	);
};

export default ChatViewer;
